import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconChevronRight,
  IconGrade,
  IconListDocs,
  IconSpec,
  IconUsers,
} from '../components/Icons';
import { useAuth } from '../context/AuthContext';

function IconHome({ active }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {active ? (
        <path
          d="M4 10.5 12 3.8l8 6.7V20a1.2 1.2 0 0 1-1.2 1.2h-4.6v-5.8H9.8v5.8H5.2A1.2 1.2 0 0 1 4 20V10.5Z"
          fill="currentColor"
        />
      ) : (
        <path
          d="M4.5 10.6 12 4.2l7.5 6.4V20a1 1 0 0 1-1 1h-4.3v-5.6H9.8V21H5.5a1 1 0 0 1-1-1v-9.4Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function IconScan() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M7 4.2H5.4A1.4 1.4 0 0 0 4 5.6V7M17 4.2h1.6A1.4 1.4 0 0 1 20 5.6V7M7 19.8H5.4A1.4 1.4 0 0 1 4 18.4V17M17 19.8h1.6a1.4 1.4 0 0 0 1.4-1.4V17"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <rect
        x="7.2"
        y="7.2"
        width="9.6"
        height="9.6"
        rx="1.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function IconMore({ active }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="6" cy="12" r={active ? 2.2 : 1.8} fill="currentColor" />
      <circle cx="12" cy="12" r={active ? 2.2 : 1.8} fill="currentColor" />
      <circle cx="18" cy="12" r={active ? 2.2 : 1.8} fill="currentColor" />
    </svg>
  );
}

const MORE_LINK_DEFS = [
  {
    to: '/customers',
    label: 'ลูกค้า',
    desc: 'รายชื่อและกรองข้อมูล',
    Icon: IconUsers,
    permission: 'customers.read',
  },
  {
    to: '/grades',
    label: 'เกรดกระดาษ',
    desc: 'Master เกรด / แกรม / หน้ากว้าง',
    Icon: IconGrade,
    permission: 'masters.read',
  },
  {
    to: '/data',
    label: 'ข้อมูล',
    desc: 'ประวัติผลสแกนทั้งหมด',
    Icon: IconListDocs,
    permission: 'inspections.read',
  },
  {
    to: '/spec',
    label: 'Spec',
    desc: 'กฎ / สเปคปัญหา',
    Icon: IconSpec,
    permission: 'masters.read',
  },
  {
    to: '/members',
    label: 'สมาชิก',
    desc: 'Role / Permission ใน Rollscan',
    Icon: IconUsers,
    permission: 'members.manage',
  },
];

function MoreMenu({ open, onClose, onNavigate, links }) {
  if (!open) return null;
  return (
    <>
      <button
        type="button"
        className="more-menu-backdrop"
        aria-label="ปิดเมนู"
        onClick={onClose}
      />
      <div className="more-menu-sheet" role="menu" aria-label="เมนูอื่นๆ">
        <div className="more-menu-handle" aria-hidden="true" />
        <p className="more-menu-title">เมนูอื่นๆ</p>
        {links.map((item) => (
          <button
            key={item.to}
            type="button"
            className="more-menu-item"
            role="menuitem"
            onClick={() => onNavigate(item.to)}
          >
            <span className="more-menu-item-icon" aria-hidden="true">
              <item.Icon size={20} />
            </span>
            <span className="more-menu-item-text">
              <strong>{item.label}</strong>
              <span>{item.desc}</span>
            </span>
            <span className="more-menu-item-chevron" aria-hidden="true">
              <IconChevronRight size={18} />
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

export default function AppLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  const moreLinks = useMemo(
    () => MORE_LINK_DEFS.filter((item) => can(item.permission)),
    [can]
  );

  const isScanRoute = pathname === '/scan';
  const isScanNav = pathname === '/scan' || pathname.startsWith('/scan/');
  const isMoreActive = moreLinks.some((item) => pathname.startsWith(item.to));

  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') setMoreOpen(false);
    }
    function onPointer(e) {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.more-menu-sheet') || target.closest('.desktop-more') || target.closest('.tab-item-more')) {
        return;
      }
      setMoreOpen(false);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [moreOpen]);

  function goMore(to) {
    setMoreOpen(false);
    navigate(to);
  }

  const roleLabel = user?.roles?.[0]
    ? String(user.roles[0]).charAt(0).toUpperCase() + String(user.roles[0]).slice(1)
    : '';

  return (
    <div className={`app-shell${isScanRoute ? ' app-shell-scan' : ''}`}>
      <header className="app-header">
        <div className="app-header-bar" aria-hidden="true" />
        <div className="app-header-inner">
          <span className="app-header-flute" aria-hidden="true" />
          <div className="brand-wrap">
            <span className="brand-mark" aria-hidden="true">
              RS
            </span>
            <div className="brand">
              <strong>Rollscan</strong>
              <span>
                ตรวจผิวกระดาษ
                {roleLabel ? ` · ${roleLabel}` : ''}
              </span>
            </div>
          </div>

          <nav className="desktop-nav" aria-label="เมนูหลัก">
            {can('dashboard.read') ? (
              <NavLink to="/" end className="desktop-nav-link">
                Dashboard
              </NavLink>
            ) : null}
            {can('inspections.create') ? (
              <NavLink
                to="/scan"
                className={({ isActive }) =>
                  `desktop-nav-link desktop-nav-scan${isActive || isScanNav ? ' active' : ''}`
                }
              >
                สแกน
              </NavLink>
            ) : null}
            {can('customers.read') ? (
              <NavLink to="/customers" className="desktop-nav-link">
                ลูกค้า
              </NavLink>
            ) : null}
            {can('inspections.read') ? (
              <NavLink to="/data" className="desktop-nav-link">
                ข้อมูล
              </NavLink>
            ) : null}
            {can('masters.read') ? (
              <NavLink to="/spec" className="desktop-nav-link">
                Spec
              </NavLink>
            ) : null}
            {can('members.manage') ? (
              <NavLink to="/members" className="desktop-nav-link">
                สมาชิก
              </NavLink>
            ) : null}
          </nav>
        </div>
      </header>

      <main className={`app-main${isScanRoute ? ' app-main-scan' : ''}`}>
        <Outlet />
      </main>

      <nav className="app-tabbar" aria-label="เมนูมือถือ">
        {can('dashboard.read') ? (
          <NavLink to="/" end className="tab-item">
            {({ isActive }) => (
              <>
                <span className="tab-icon">
                  <IconHome active={isActive} />
                </span>
                <span className="tab-label">Dashboard</span>
              </>
            )}
          </NavLink>
        ) : null}

        {can('inspections.create') ? (
          <NavLink to="/scan" className="tab-item tab-item-scan">
            {({ isActive }) => (
              <>
                <span className={`tab-scan-btn${isActive || isScanNav ? ' active' : ''}`}>
                  <IconScan />
                </span>
                <span className="tab-label">สแกน</span>
              </>
            )}
          </NavLink>
        ) : null}

        {moreLinks.length > 0 ? (
          <button
            type="button"
            className={`tab-item tab-item-more${isMoreActive || moreOpen ? ' active' : ''}`}
            aria-expanded={moreOpen}
            aria-haspopup="menu"
            onClick={() => setMoreOpen((v) => !v)}
          >
            <span className="tab-icon">
              <IconMore active={isMoreActive || moreOpen} />
            </span>
            <span className="tab-label">อื่นๆ</span>
          </button>
        ) : null}
      </nav>

      <MoreMenu
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onNavigate={goMore}
        links={moreLinks}
      />
    </div>
  );
}
